import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Trash2, CalendarDays, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/use-toast';
import {
  getHolidays,
  createHoliday,
  updateHoliday,
  deleteHoliday,
} from './Services/parametrizacion.services';

const TIPOS = ['Nacional', 'Regional', 'Religioso', 'Otro'];

const formatear = (fecha) => {
  if (!fecha) return '—';
  const d = new Date(fecha);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' });
};

const FestivosPage = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ fecha: '', nombre: '', descripcion: '', tipo: 'Nacional' });
  const [aEliminar, setAEliminar] = useState(null);

  const cargar = async () => {
    setLoading(true);
    try {
      const res = await getHolidays();
      const lista = res?.data || [];
      lista.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
      setItems(lista);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
  }, []);

  const abrirNuevo = () => {
    setEditando(null);
    setForm({ fecha: '', nombre: '', descripcion: '', tipo: 'Nacional' });
    setOpen(true);
  };

  const abrirEdicion = (item) => {
    setEditando(item);
    const d = item.fecha ? new Date(item.fecha) : null;
    setForm({
      fecha:
        d && !Number.isNaN(d.getTime())
          ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
          : '',
      nombre: item.nombre || '',
      descripcion: item.descripcion || '',
      tipo: item.tipo || 'Nacional',
    });
    setOpen(true);
  };

  const guardar = async (e) => {
    e.preventDefault();
    if (!form.fecha || !form.nombre.trim()) {
      toast({
        title: 'Faltan datos',
        description: 'La fecha y el nombre son obligatorios.',
        variant: 'destructive',
      });
      return;
    }
    try {
      if (editando) {
        await updateHoliday(editando._id, form);
        toast({ title: 'Éxito', description: 'Festivo actualizado.' });
      } else {
        await createHoliday(form);
        toast({ title: 'Éxito', description: 'Festivo creado.' });
      }
      setOpen(false);
      cargar();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const confirmarEliminar = async () => {
    try {
      await deleteHoliday(aEliminar._id);
      toast({ title: 'Éxito', description: 'Festivo eliminado.' });
      setAEliminar(null);
      cargar();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const porAnio = useMemo(() => {
    return items.reduce((acc, item) => {
      const d = new Date(item.fecha);
      const anio = Number.isNaN(d.getTime()) ? 'Sin fecha' : d.getFullYear();
      acc[anio] = (acc[anio] || 0) + 1;
      return acc;
    }, {});
  }, [items]);

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button onClick={abrirNuevo}>
            <Plus className="mr-2 h-4 w-4" /> Crear festivo
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <CardStat
            icon={CalendarDays}
            titulo="Festivos"
            valor={loading ? '—' : items.length}
            descripcion="Total registrados"
          />
          {Object.entries(porAnio).map(([anio, total]) => (
            <CardStat key={anio} titulo={String(anio)} valor={total} descripcion="Festivos del año" />
          ))}
        </div>

        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Festivo</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Descripción</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground">
                    Cargando...
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground">
                    Todavía no hay festivos registrados.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item._id}>
                    <TableCell className="whitespace-nowrap">{formatear(item.fecha)}</TableCell>
                    <TableCell className="font-medium">{item.nombre}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{item.tipo}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {item.descripcion || '—'}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button variant="ghost" size="icon" onClick={() => abrirEdicion(item)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive"
                        onClick={() => setAEliminar(item)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editando ? 'Editar festivo' : 'Crear festivo'}</DialogTitle>
            <DialogDescription>
              Los festivos marcan qué días se liquidan con recargo especial en la nómina.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={guardar} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="hol-fecha">Fecha</Label>
              <Input
                id="hol-fecha"
                type="date"
                value={form.fecha}
                onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hol-nombre">Nombre</Label>
              <Input
                id="hol-nombre"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                placeholder="Ej. Día de la Independencia"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hol-tipo">Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger id="hol-tipo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="hol-desc">Descripción</Label>
              <Input
                id="hol-desc"
                value={form.descripcion}
                onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                placeholder="Opcional"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">{editando ? 'Guardar cambios' : 'Crear'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!aEliminar} onOpenChange={(o) => !o && setAEliminar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este festivo?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará “{aEliminar?.nombre}”. Las liquidaciones ya guardadas no cambian, pero
              los próximos cálculos dejarán de contarlo como festivo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmarEliminar}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
};

const CardStat = ({ icon: Icon, titulo, valor, descripcion }) => (
  <div className="rounded-lg border bg-card p-4">
    <div className="flex items-center gap-2">
      {Icon && (
        <div className="p-1.5 rounded-md bg-primary/15">
          <Icon className="h-4 w-4 text-primary" />
        </div>
      )}
      <span className="text-sm text-muted-foreground">{titulo}</span>
    </div>
    <div className="text-2xl font-bold mt-1">{valor}</div>
    <p className="text-xs text-muted-foreground">{descripcion}</p>
  </div>
);

export default FestivosPage;